import {createContext, useContext, useEffect, useMemo} from 'react';
import {useSigninStatus} from '../auth/hooks/useSigninStatus';
import {useCloudLogger} from '../firebase/hooks/useCloudLogger';
import {PropsWithRequiredChildren} from '../react/types';
import {ConsoleLogger} from '#common/logging/ConsoleLogger';
import {LoggerLike, logger} from '#common/logging/logger';

const consoleLogger = new ConsoleLogger();

const LoggerContext = createContext<LoggerLike>(consoleLogger);

/**
 * Provides a logger instance via React context.
 * - When signed in: provides cloud logger (logs to Firebase)
 * - When signed out: provides console logger
 *
 * Also syncs the singleton `logger` for non-React code.
 */
export function LoggerProvider({children}: PropsWithRequiredChildren) {
  const {isSignedIn} = useSigninStatus();
  const cloudLogger = useCloudLogger();

  const currentLogger = useMemo(() => (isSignedIn ? cloudLogger : consoleLogger), [cloudLogger, isSignedIn]);

  // Sync singleton logger for non-React code (utilities, services, etc.)
  useEffect(() => {
    logger.setLogger(currentLogger);
  }, [currentLogger]);

  return <LoggerContext value={currentLogger}>{children}</LoggerContext>;
}

/**
 * Hook to access the current logger instance.
 * Use this in React components instead of importing the singleton directly.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const log = useLogger();
 *   log.info('Component rendered');
 *   return <div>...</div>;
 * }
 * ```
 */
export function useLogger(): LoggerLike {
  return useContext(LoggerContext);
}
